import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar/Sidebar';
import './KnowledgePage.scss';
import { NewsAndEventsProps } from '../NewsAndEvents/types';
import { connect, useDispatch } from 'react-redux';
import TextEditor from '../../components/TextEditor/TextEditor';
import { AppState } from '../../store/rootReducer';
import { getUserId } from '../../store/auth/selectors';
import { collection, deleteDoc, doc, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import SidebarSecond from '../../components/Sidebar/SidebarSecond';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    MenuItem,
    OutlinedInput,
    Select,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import { autoId } from '../../utils/UidGenerator';
import AddImage from '../../components/AddImage/AddImage';
import strings from '../../resources/strings';
import { LanguagesBar } from '../../components/LanguagesBar';
import { getLanguagesList } from '../../store/languages/selectors';
import { requestGetList } from '../../store/languages/actions';
import consts from './consts';
import DbCollections from '../../resources/enums/DbCollections';
import { knowledgeInterface, knowledgeInterfaceCategory } from './KnowledgePageInterfaces';
import KnowledgePageModal from './KnowledgePageModal';
import SnackbarMessage from '../../components/SnackbarMessage/SnackbarMessage';
import { validateLanguages } from '../../utils/Validator';
import NotificationTypes from '../../resources/enums/NotificationTypes';
import { sendNotification } from '../../utils/Notifications';
import { storage } from '../../resources/storage';

const KnowledgePage: React.FC<NewsAndEventsProps> = (props) => {
    const [error, setError] = useState<boolean>(false);
    const [typeList, setTypeList] = useState<knowledgeInterface[]>([]);
    const [typeListFiltered, setTypeListFiltered] = useState<knowledgeInterface[]>([]);
    const [categoryList, setCategoryList] = useState<knowledgeInterfaceCategory[]>([]);
    const [currentType, setCurrentType] = useState<knowledgeInterfaceCategory>();
    const [actualEvent, setActualEvent] = useState<knowledgeInterface>();
    const [languageValue, setLanguageValue] = React.useState('pl');
    const [openModal, setOpenModal] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');
    const externalContentRef = collection(db, DbCollections.ExternalContent);
    const externalContentRefCategory = collection(db, DbCollections.ExternalContentCategories);
    const lang = 'en';
    const dispatch = useDispatch();
    const articleTypeId = '2';

    useEffect(() => {
        firebaseEvents.getNews();
        firebaseEvents.getCategory();
        setCurrentType(consts.knowledgeTypeList[0]);
        dispatch(requestGetList());
    }, []);
    useEffect(() => {
        if (typeList.length === 0) {
            return;
        }
        if (!actualEvent) {
            setActualEvent(typeList[0]);
        }
    }, [typeList]);
    useEffect(() => {
        const currentListName = currentType?.title?.[lang]?.toLowerCase();
        setTypeListFiltered(typeList.filter((item) => item.type.toLowerCase() === currentListName));
    }, [currentType, typeList]);

    const createEmptyRecord = () => {
        let record = {};
        props.languages.forEach((item) => {
            record = { ...record, [item.code]: '' };
        });
        return record;
    };

    const createNewEvent = () => {
        const newEventToAdd = {
            author: createEmptyRecord(),
            id: autoId(),
            category: categoryList[0].id,
            image: '',
            highlight: false,
            url: '',
            type: currentType?.title?.[lang]?.toLowerCase(),
            publishFrom: null,
            publishTo: null,
            title: createEmptyRecord(),
        } as knowledgeInterface;
        if (currentType?.id === articleTypeId) newEventToAdd.text = createEmptyRecord();
        typeListFiltered.push(newEventToAdd);
        setActualEvent(newEventToAdd);
    };

    const firebaseEvents = {
        getNews: async () => {
            const data = await getDocs(externalContentRef);

            const events = await data.docs.map(
                (news) =>
                    ({
                        ...news.data(),
                        id: news.id,
                        publishFrom: news.data().publishFrom ? news.data().publishFrom.toDate() : null,
                        publishTo: news.data().publishTo ? news.data().publishTo.toDate() : null,
                    } as knowledgeInterface),
            );
            setTypeList(events);
            setActualEvent(events[0]);
        },
        getCategory: async () => {
            const data = await getDocs(externalContentRefCategory);

            const events = await data.docs.map(
                (news) =>
                    ({
                        ...news.data(),
                        id: news.id,
                        title: news.data().title,
                    } as knowledgeInterfaceCategory),
            );
            setCategoryList(events);
        },
        update: async (message: string) => {
            if (!actualEvent) return;
            const newItem = { ...actualEvent };
            if (!validateLanguages([newItem], props.languages.length)) {
                if (setError) setError(true);
                return;
            }
            const document = doc(db, DbCollections.ExternalContent, newItem.id);
            sendNotification(newItem, document, NotificationTypes.Article, dispatch);
            await setDoc(document, {
                ...newItem,
            });
            const currentIds = typeList.map((item) => {
                return item.id;
            });
            if (currentIds.includes(newItem.id))
                setTypeList(
                    typeList.map((item) => {
                        return item.id !== newItem.id ? item : newItem;
                    }),
                );
            else setTypeList(typeList.length > 0 ? [...typeList, newItem] : [newItem]);

            setSuccessMessage(message);
        },
        delete: async (id: string) => {
            const eventDoc = doc(db, DbCollections.ExternalContent, id);
            const actualIndex = typeList.findIndex((news: knowledgeInterface) => news.id === id);
            await deleteDoc(eventDoc);
            setTypeList(typeList.filter((news: knowledgeInterface) => news.id !== id));
            setActualEvent(typeList[actualIndex - 1]);
        },
    };

    return (
        <div className="content">
            <Sidebar></Sidebar>
            <SidebarSecond
                data={consts.knowledgeTypeList}
                actualItemId={currentType?.id}
                title={strings.knowledge.title}
                isAddNew={false}
                hideStatus={true}
                setActualItemId={(id: string) => {
                    setCurrentType(consts.knowledgeTypeList.find((el) => el.id === id));
                    setActualEvent(undefined);
                }}
                actualLanguage={lang}
            ></SidebarSecond>
            <SidebarSecond
                data={typeListFiltered}
                actualItemId={actualEvent?.id}
                createNewItem={() => createNewEvent()}
                title={currentType?.title?.[lang]}
                isAddNew={true}
                hideStatus={false}
                setActualItemId={(id: string) => {
                    setActualEvent(typeListFiltered.find((el) => el.id === id));
                    setSuccessMessage('');
                }}
                actualLanguage={languageValue}
            ></SidebarSecond>
            {!actualEvent ? null : (
                <div className="main">
                    <LanguagesBar
                        languages={props.languages}
                        selectedLanguage={languageValue}
                        handleChange={setLanguageValue}
                    ></LanguagesBar>
                    <Box sx={{ marginTop: '2rem' }}>
                        <OutlinedInput
                            placeholder={`${currentType?.title?.[lang]}${strings.knowledge.placeholderTitle}`}
                            value={actualEvent.title?.[languageValue]}
                            onChange={(event) => {
                                setActualEvent({
                                    ...actualEvent,
                                    title: { ...actualEvent.title, [languageValue]: event.target.value },
                                });
                            }}
                            sx={{
                                margin: '2rem auto 2rem 0',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '1rem',
                                maxWidth: '600px',
                                width: '100%',
                            }}
                        />
                    </Box>
                    <Box sx={{ marginTop: '2rem' }}>
                        <OutlinedInput
                            value={actualEvent.author?.[languageValue]}
                            placeholder={`${currentType?.title?.[lang]}${strings.knowledge.placeholderAuthor}`}
                            onChange={(event) => {
                                setActualEvent({
                                    ...actualEvent,
                                    author: { ...actualEvent.author, [languageValue]: event.target.value },
                                });
                            }}
                            sx={{
                                margin: '2rem auto 2rem 0',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '1rem',
                                maxWidth: '600px',
                                width: '100%',
                            }}
                        />
                    </Box>
                    <Box>
                        <FormControl sx={{ m: 1, minWidth: 80, margin: '0 0 1rem 0' }}>
                            <Select
                                labelId="demo-simple-select-autowidth-label"
                                id="demo-simple-select-autowidth"
                                onChange={(event) => {
                                    setActualEvent({
                                        ...actualEvent,
                                        category: event.target.value,
                                    });
                                }}
                                value={actualEvent.category}
                                autoWidth
                            >
                                {categoryList.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.title?.[languageValue]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={actualEvent.highlight}
                                        onChange={(event) => {
                                            setActualEvent({
                                                ...actualEvent,
                                                highlight: event.target.checked,
                                            });
                                        }}
                                    />
                                }
                                label={strings.knowledge.labelTitle}
                            />
                        </FormGroup>
                    </Box>
                    <Box>
                        <h3 className="main-title">{strings.newsAndEvents.image}</h3>
                        <AddImage
                            path={storage.externalContent}
                            actualEventId={actualEvent.id}
                            actualEventFile={actualEvent.image}
                            setGetImgAddress={(url: string) =>
                                setActualEvent({
                                    ...actualEvent,
                                    image: url,
                                })
                            }
                        ></AddImage>
                    </Box>
                    {currentType?.id !== articleTypeId ? null : (
                        <>
                            <Box>
                                <h3 className="main-title">{strings.newsAndEvents.text}</h3>
                                <TextEditor
                                    value={actualEvent.text?.[languageValue]}
                                    getText={(content: string) => {
                                        setActualEvent({
                                            ...actualEvent,
                                            text: { ...actualEvent.text, [languageValue]: content },
                                        });
                                    }}
                                    setEventId={actualEvent.id}
                                    languageValue={languageValue}
                                />
                            </Box>
                            <Box>
                                <h3 className="main-title">{strings.newsAndEvents.file}</h3>

                                <AddImage
                                    path={storage.externalContent}
                                    actualEventId={actualEvent.id}
                                    actualEventFile={actualEvent.url}
                                    setGetImgAddress={(url: string) =>
                                        setActualEvent({
                                            ...actualEvent,
                                            url,
                                        })
                                    }
                                    fileType={consts.inputContent.pdf}
                                    acceptedFormat={consts.formats.pdf}
                                ></AddImage>
                            </Box>
                        </>
                    )}

                    {currentType?.id === articleTypeId ? null : (
                        <Box>
                            <OutlinedInput
                                value={actualEvent.url}
                                placeholder={strings.knowledge.placeholderLink}
                                onChange={(event) => {
                                    setActualEvent({
                                        ...actualEvent,
                                        url: event.target.value,
                                    });
                                }}
                                sx={{
                                    margin: '2rem auto 2rem 0',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '1rem',
                                    maxWidth: '600px',
                                    width: '100%',
                                }}
                            />
                        </Box>
                    )}

                    {successMessage.length > 0 ? (
                        <Box>
                            <Alert
                                sx={{
                                    fontSize: '1.6rem',
                                    margin: '1rem auto 0 auto',
                                    maxWidth: '600px',
                                }}
                                variant="outlined"
                                severity="success"
                            >
                                {successMessage}
                            </Alert>
                        </Box>
                    ) : null}
                    <Box>
                        <SnackbarMessage
                            serverity="error"
                            message={error ? strings.languagesValidation.errorMessage : ''}
                            clearMessage={() => {
                                setError(false);
                            }}
                        ></SnackbarMessage>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            paddingRight: '2rem',
                            marginTop: '2rem',
                        }}
                    >
                        <Button
                            onClick={() => {
                                if (actualEvent && actualEvent.id) firebaseEvents.delete(actualEvent.id);
                            }}
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                        >
                            {strings.newsAndEvents.buttons.delete}
                        </Button>
                        <Button
                            onClick={() => {
                                if (!actualEvent) return;
                                setActualEvent({
                                    ...actualEvent,
                                });
                                firebaseEvents.update(strings.newsAndEvents.successSave);
                            }}
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}
                        >
                            {strings.newsAndEvents.buttons.save}
                        </Button>
                        <Button
                            onClick={() => {
                                setOpenModal(true);
                                if (!actualEvent) return;
                            }}
                            startIcon={<SendIcon />}
                            variant="contained"
                            href="#contained-buttons"
                        >
                            {strings.newsAndEvents.buttons.publish}
                        </Button>

                        <KnowledgePageModal
                            openModal={openModal}
                            actualEvent={actualEvent}
                            setActualEvent={setActualEvent}
                            setOpenModal={setOpenModal}
                            typeList={typeList}
                            setSuccessMessage={setSuccessMessage}
                            firebaseEventsUpdate={firebaseEvents.update}
                        />
                    </Box>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: AppState) {
    return {
        userId: getUserId(state),
        languages: getLanguagesList(state),
    };
}

export default connect(mapStateToProps)(KnowledgePage);
